import React from "react";

/**
 * Reusable Button Component
 *
 * @param {string} variant - Button style variant (primary, secondary, black, rounded, ghost, danger, success, neutral, tab)
 * @param {React.ReactNode} children - Button content
 * @param {function} onClick - Click handler
 * @param {boolean} disabled - Disabled state
 * @param {string} className - Additional CSS classes
 * @param {string} type - Button type (button, submit, reset)
 * @param {string} title - Tooltip text
 *
 * @example
 * <Button variant="primary" onClick={handleClick}>
 *   <Icon className="h-5 w-5" />
 *   Click Me
 * </Button>
 */

const buttonVariants = {
  primary:
    "flex items-center space-x-2 px-3 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-lg shadow-sm hover:bg-primary/90 transition-colors",
  secondary:
    "flex items-center space-x-2 px-3 py-2 text-sm font-medium text-foreground bg-background border border-input rounded-lg shadow-sm hover:bg-muted transition-colors",
  black:
    "flex items-center gap-2 whitespace-nowrap px-4 py-2 text-sm bg-black text-white rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 transition-colors",
  rounded:
    "flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-full text-sm font-semibold shadow hover:bg-primary/90 transition-colors",
  ghost: "p-2 rounded-full hover:bg-gray-100 transition-colors",
  danger:
    "px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 transition-colors",
  success:
    "flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium",
  neutral:
    "px-4 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors",
  tab: "px-4 py-2 rounded-lg text-sm transition-colors",
  tabActive: "bg-black text-white",
  tabInactive: "bg-gray-100 text-gray-700 hover:bg-gray-200",
};

const Button = ({
  variant = "primary",
  children,
  onClick,
  disabled = false,
  className = "",
  type = "button",
  title,
  ...props
}) => {
  const baseClass = buttonVariants[variant] || buttonVariants.primary;
  const disabledClass = disabled ? "opacity-50 cursor-not-allowed" : "";

  return (
    <button
      type={type}
      className={`${baseClass} ${disabledClass} ${className}`.trim()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      {...props}
    >
      {children}
    </button>
  );
};

export default Button;
